import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { map, withLatestFrom, tap } from 'rxjs/operators';
import * as AccessManagementActions from './access-management.actions';
import { selectSelectedTabIndex } from './access-management.selectors';
import { AccessManagementTab } from './access-management.models';

@Injectable()
export class AccessManagementEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private router = inject(Router);

  // Effect to sync tab changes with URL query params
  syncTabWithUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccessManagementActions.setSelectedTab),
        withLatestFrom(this.store.select(selectSelectedTabIndex)),
        tap(([action]) => {
          const tabNames: AccessManagementTab[] = ['roles', 'user-collections', 'ou-collections', 'designations'];
          const tabName = tabNames[action.tabIndex];

          if (tabName) {
            // Update URL without triggering navigation
            this.router.navigate([], {
              queryParams: { tab: tabName },
              queryParamsHandling: 'merge',
              replaceUrl: true
            });
          }
        })
      ),
    { dispatch: false }
  );

  // Effect to load tab from URL on init
  loadTabFromUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccessManagementActions.initAccessManagement),
      map(() => {
        // Get query params from current URL
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');

        if (tabParam) {
          const tabMap: Record<AccessManagementTab, number> = {
            'roles': 0,
            'user-collections': 1,
            'ou-collections': 2,
            'designations': 3
          };

          const tabIndex = tabMap[tabParam as AccessManagementTab];
          if (tabIndex !== undefined) {
            return AccessManagementActions.setSelectedTab({ tabIndex });
          }
        }

        return AccessManagementActions.loadAccessManagementSuccess();
      })
    )
  );
}